import { useCallback, useRef, useState } from 'react';

export type FloatingAssistCorner = 'top-right' | 'bottom-right';

export type CornerArrowPlacement = 'top' | 'bottom';

export interface CornerArrowOption {
  target: FloatingAssistCorner;
  placement: CornerArrowPlacement;
  rotation: number;
  label: string;
}

const STORAGE_KEY = 'floatingAssistCorner';

const CORNER_ARROWS: Record<FloatingAssistCorner, CornerArrowOption[]> = {
  'bottom-right': [
    { target: 'top-right', placement: 'top', rotation: 0, label: 'Move to top right' },
  ],
  'top-right': [
    { target: 'bottom-right', placement: 'bottom', rotation: 180, label: 'Move to bottom right' },
  ],
};

const loadCorner = (): FloatingAssistCorner => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'top-right' || stored === 'bottom-right') return stored;
  } catch {
    // ignore storage errors
  }
  return 'bottom-right';
};

export const getCornerArrowOptions = (current: FloatingAssistCorner): CornerArrowOption[] =>
  CORNER_ARROWS[current];

export const useFloatingAssistPosition = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const cornerRef = useRef<FloatingAssistCorner>(loadCorner());
  const [corner, setCorner] = useState<FloatingAssistCorner>(cornerRef.current);

  const moveToCorner = useCallback((nextCorner: FloatingAssistCorner) => {
    cornerRef.current = nextCorner;
    setCorner(nextCorner);

    try {
      localStorage.setItem(STORAGE_KEY, nextCorner);
    } catch {
      // ignore storage errors
    }
  }, []);

  const anchorClass = `floating-assist-root--anchor-${corner}`;

  return {
    rootRef,
    anchorClass,
    corner,
    moveToCorner,
    cornerOptions: getCornerArrowOptions(corner),
  };
};
